import { Address } from 'modules/sleepNewLeads/PaymentTypeSelection/types/address'
import { BankAccountInput } from 'modules/sleepNewLeads/PaymentTypeSelection/types/bankAccountInput'
import { CreditCardInput } from 'modules/sleepNewLeads/PaymentTypeSelection/types/creditCardInput'
import { PaymentProfile } from 'modules/sleepNewLeads/PaymentTypeSelection/types/paymentProfile'
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'


interface PaymentStore {
  customerProfileId: number | undefined
  setCustomerProfileId: ( id: number ) => void
  paymentProfiles: PaymentProfile[]
  newCardDetails: CreditCardInput
  isNewCardValid: boolean
  newBankDetails: BankAccountInput
  isNewBankValid: boolean
  splitPayment: boolean | null
  savePaymentProfile: boolean
  payLater: boolean | null
  setPayLater: ( payLater: boolean ) => void
  setSavePaymentProfile: ( save: boolean ) => void
  setSplitPayment: ( split: boolean ) => void
  setIsNewBankValid: ( valid: boolean ) => void
  setNewBankDetails: ( details: BankAccountInput ) => void
  setIsNewCardValid: ( valid: boolean ) => void
  setNewCardDetails: ( details: CreditCardInput ) => void
  setPaymentProfiles: ( profiles: PaymentProfile[] ) => void
  addPaymentProfile: ( profile: PaymentProfile ) => void
  removePaymentProfile: ( profileId: string ) => void
  billingAddressSameAsShipping: boolean
  toggleBillingAddressSameAsShipping: () => void
  setBillingAddressSameAsShipping: ( same: boolean ) => void
  billingAddress: Address
  setBillingAddress: ( address: Address ) => void
  selectedPaymentProfile: string | undefined
  billingAddressValid: boolean
  setBillingAddressValid: ( valid: boolean ) => void
  setSelectedPaymentProfile: ( paymentProfileId: string ) => void
  resetPaymentStore: () => void
}

export const usePaymentStore = create<PaymentStore>()(
  persist(
    ( set ) => ({
      customerProfileId: undefined as number | undefined,
      paymentProfiles: [],
      savePaymentProfile: false,
      billingAddressSameAsShipping: true,
      payLater: null,
      setPayLater: ( payLater: boolean ) => set({
        payLater
      }),
      billingAddress: {
        street: ``,
        street2: ``,
        city: ``,
        state: ``,
        zipCode: ``
      },
      newCardDetails: {
        nameOnCard: ``,
        cardNumber: ``,
        expirationMonth: ``,
        expirationYear: ``,
        CVV: ``
      },
      newBankDetails: {
        routingNumber: ``,
        accountNumber: ``,
        confirmAccountNumber: ``,
        accountType: ``,
        nameOnAccount: ``
      },
      isNewBankValid: false,
      splitPayment: null,
      setSavePaymentProfile: ( save: boolean ) =>
        set({
          savePaymentProfile: save
        }),
      setSplitPayment: ( split: boolean ) => set({
        splitPayment: split
      }),
      setIsNewBankValid: ( valid: boolean ) => set({
        isNewBankValid: valid
      }),
      setNewBankDetails: ( details: BankAccountInput ) =>
        set({
          newBankDetails: details
        }),
      billingAddressValid: true,
      setBillingAddressValid: ( valid: boolean ) =>
        set({
          billingAddressValid: valid
        }),
      isNewCardValid: false,
      setIsNewCardValid: ( valid: boolean ) => set({
        isNewCardValid: valid
      }),
      setNewCardDetails: ( details: CreditCardInput ) =>
        set({
          newCardDetails: details
        }),
      selectedPaymentProfile: undefined,
      setSelectedPaymentProfile: ( paymentProfileId: string ) =>
        set({
          selectedPaymentProfile: paymentProfileId
        }),
      setBillingAddress: ( address: Address ) => set({
        billingAddress: address
      }),
      toggleBillingAddressSameAsShipping: () =>
        set( ( state ) => ({
          billingAddressSameAsShipping: !state.billingAddressSameAsShipping
        }) ),
      setBillingAddressSameAsShipping: ( same: boolean ) =>
        set({
          billingAddressSameAsShipping: same
        }),
      setCustomerProfileId: ( id: number ) => set({
        customerProfileId: id
      }),
      setPaymentProfiles: ( profiles: PaymentProfile[] ) =>
        set({
          paymentProfiles: profiles
        }),
      addPaymentProfile: ( profile: PaymentProfile ) =>
        set( ( state ) => ({
          paymentProfiles: [ ...state.paymentProfiles, profile ]
        }) ),
      removePaymentProfile: ( profileId: string ) =>
        set( ( state ) => ({
          paymentProfiles: state.paymentProfiles.filter(
            ( profile ) => profile.customerPaymentProfileId !== profileId
          )
        }) ),
      resetPaymentStore: () =>
        set({
          customerProfileId: undefined,
          paymentProfiles: [],
          savePaymentProfile: false,
          billingAddressSameAsShipping: true,
          billingAddress: {
            street: ``,
            street2: ``,
            city: ``,
            state: ``,
            zipCode: ``
          },
          newCardDetails: {
            nameOnCard: ``,
            cardNumber: ``,
            expirationMonth: ``,
            expirationYear: ``,
            CVV: ``
          },
          newBankDetails: {
            routingNumber: ``,
            accountNumber: ``,
            accountType: ``,
            nameOnAccount: ``
          },
          isNewBankValid: false,
          isNewCardValid: false,
          splitPayment: null,
          billingAddressValid: true,
          selectedPaymentProfile: undefined
        })
    }),
    {
      name: `payment-storage`,
      storage: createJSONStorage( () => sessionStorage )
    }
  )
)